<template>
  <div>
    <v-container grid-list-md>
      <!-- HEADER -->
      <v-layout row wrap>
        <v-flex xs12>
          <h1>{{ $t('security_my_parks') }}</h1>
          <p>{{ $t('security_my_parks_description') }}</p>
        </v-flex>
      </v-layout>

      <!-- LOADING STATE -->
      <v-layout row wrap v-if="isSecurityLoading">
        <v-flex xs12>
          <v-progress-linear indeterminate></v-progress-linear>
        </v-flex>
      </v-layout>

      <!-- ERROR STATE -->
      <v-layout row wrap v-if="getSecurityError">
        <v-flex xs12>
          <v-alert type="error" dismissible>
            {{ getSecurityError }}
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- PARKS LIST (Already Linked) -->
      <v-layout row wrap v-if="myParks.length > 0 && !isSecurityLoading">
        <v-flex xs12>
          <h2 class="primary--text mb-4">{{ $t('security_my_parks') }} ({{ myParks.length }})</h2>
        </v-flex>
        <v-flex xs12 sm6 v-for="park in myParks" :key="`linked-${park.id}`" class="pa-2 d-flex">
          <v-card class="elevation-2 rounded-lg park-card d-flex flex-column" :style="parkCardBorderStyle(park.status)">
            <v-card-text class="pa-4 flex-grow-1">
              <div class="d-flex align-center mb-3">
                  <v-icon :color="getStatusColor(park.status)" size="24">location_on</v-icon>
                <div class="text-truncate">
                  <h3 class="subheading font-weight-bold mb-0 text-truncate">{{ park.park_name || park.park_nombre }}</h3>
                  <small class="grey--text">{{ park.security_company_name }}</small>
                </div>
              </div>

              <v-chip :color="getStatusColor(park.status)" text-color="white" small class="mb-3 ml-0">
                <v-icon left small>{{ getStatusIcon(park.status) }}</v-icon>
                {{ park.status_display || park.status }}
              </v-chip>

              <v-divider class="mb-3"></v-divider>

              <div class="mb-1">
                <small class="grey--text text-uppercase">{{ park.status === 'PENDING' ? 'Solicitado' : 'Desde' }}</small>
                <p class="body-2 font-weight-bold mb-0">{{ formatDate(park.created_at) }}</p>
              </div>

              <v-alert v-if="park.status === 'PENDING'" type="info" :value="true" dense class="mt-3 mb-0 caption">
                Este {{ locationName }} solicitó tus servicios de seguridad.
              </v-alert>
            </v-card-text>

            <v-card-actions class="pa-3 pt-0 action-block">
              <!-- Contrato pendiente: aceptar / rechazar -->
              <div v-if="park.status === 'PENDING'" class="action-column">
                <v-btn
                  color="success"
                  depressed
                  class="ma-0 mb-2"
                  :loading="respondingParkId === park.id"
                  @click="respondToPark(park, 'accept')"
                >
                  <v-icon left small>check_circle</v-icon>
                  Aceptar
                </v-btn>
                <v-btn
                  color="error"
                  outline
                  class="ma-0"
                  :loading="respondingParkId === park.id"
                  @click="respondToPark(park, 'reject')"
                >
                  <v-icon left small>cancel</v-icon>
                  Rechazar
                </v-btn>
              </div>

              <!-- Contrato activo: asignar guardias / desvincular -->
              <div v-else-if="park.status === 'ACTIVE'" class="action-column">
                <v-btn color="primary" depressed class="ma-0 mb-2" @click="goToAssignGuards(park.id)">
                  <v-icon left small>assignment_ind</v-icon>
                  {{ $t('security_assign_guards') }}
                </v-btn>
                <v-btn color="error" outline class="ma-0" @click="openUnlinkDialog(park)">
                  {{ $t('security_unlink') }}
                </v-btn>
              </div>

              <!-- Rechazado: solo opción de quitar de la lista -->
              <div v-else class="action-column">
                <v-btn color="grey darken-1" outline class="ma-0" @click="openUnlinkDialog(park)">
                  {{ $t('security_unlink') }}
                </v-btn>
              </div>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- EMPTY STATE - No linked parks -->
      <v-layout row wrap v-if="myParks.length === 0 && !isSecurityLoading">
        <v-flex xs12>
          <v-alert type="info" :value="true">
            No tienes {{ locationNamePlural }} vinculados todavía. Los {{ locationNamePlural }} te enviarán solicitudes de vinculación y aparecerán aquí.
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- UNLINK DIALOG -->
      <v-dialog v-model="showUnlinkDialog" max-width="400px">
        <v-card>
          <v-card-title>{{ $t('security_confirm_unlink_park') }}</v-card-title>
          <v-card-text>
            <p v-if="selectedPark">
              {{ $t('confirm_unlink_park_msg') }}
              <strong>{{ selectedPark.park_name || selectedPark.park_nombre }}</strong>?
            </p>
            <v-alert type="warning">
              {{ $t('security_unlink_warning') }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" flat @click="showUnlinkDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn color="error" flat @click="unlinkPark" :loading="isSecurityLoading">
              {{ $t('security_unlink') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { FETCH_MY_PARKS, UNLINK_PARK, RESPOND_TO_PARK_CONTRACT } from '../../../store/modules/security'
import { USER_REQUEST } from '../../../store/actions/user'
import { mapGetters } from 'vuex'

export default {
  name: 'MyParks',
  data() {
    return {
      showUnlinkDialog: false,
      selectedPark: null,
      respondingParkId: null
    }
  },
  computed: {
    ...mapGetters(['getToken', 'getProfile']),
    ...mapGetters('security', ['getMyParks', 'isSecurityLoading', 'getSecurityError']),
    myParks() {
      return this.getMyParks
    },
    locationName() {
      const profile = this.getProfile || {}
      return profile.is_building ? 'edificio corporativo' : 'parque'
    },
    locationNamePlural() {
      const profile = this.getProfile || {}
      return profile.is_building ? 'edificios corporativos' : 'parques industriales'
    }
  },
  methods: {
    getStatusColor(status) {
      const colors = {
        'ACTIVE': 'success',
        'PENDING': 'warning',
        'REJECTED': 'error',
        'INACTIVE': 'grey'
      }
      return colors[status] || 'grey'
    },
    getStatusBgColor(status) {
      const colors = {
        'ACTIVE': 'green lighten-4',
        'PENDING': 'amber lighten-4',
        'REJECTED': 'red lighten-4',
        'INACTIVE': 'grey lighten-3'
      }
      return colors[status] || 'grey lighten-3'
    },
    getStatusIcon(status) {
      const icons = {
        'ACTIVE': 'check_circle',
        'PENDING': 'schedule',
        'REJECTED': 'cancel',
        'INACTIVE': 'block'
      }
      return icons[status] || 'help'
    },
    parkCardBorderStyle(status) {
      const borderColors = {
        'ACTIVE': '#4CAF50',
        'PENDING': '#FFC107',
        'REJECTED': '#F44336',
        'INACTIVE': '#9E9E9E'
      }
      return `border-left: 4px solid ${borderColors[status] || '#9E9E9E'};`
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    openUnlinkDialog(park) {
      this.selectedPark = park
      this.showUnlinkDialog = true
    },
    unlinkPark() {
      this.$store.dispatch(`security/${UNLINK_PARK}`, {
        token: this.getToken,
        parkId: this.selectedPark.park
      })
      .then(() => {
        this.showUnlinkDialog = false
        this.$store.commit('snackbar_change_message', this.$t('security_park_unlinked_success'))
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_unlinking_park'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
    },
    respondToPark(park, action) {
      this.respondingParkId = park.id
      this.$store.dispatch(`security/${RESPOND_TO_PARK_CONTRACT}`, {
        token: this.getToken,
        contractId: park.id,
        action: action
      })
      .then(() => {
        const actionText = action === 'accept' ? 'aceptada' : 'rechazada'
        this.$store.commit('snackbar_change_message', `Solicitud del ${this.locationName} ${actionText} correctamente`)
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', `Error al responder a la solicitud del ${this.locationName}`)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
      .finally(() => {
        this.respondingParkId = null
      })
    },
    goToAssignGuards(parkId) {
      this.$router.push(`/security/assign-guards-to-park/${parkId}`)
    },
    fetchData() {
      this.$store.dispatch(`security/${FETCH_MY_PARKS}`, this.getToken)
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }

    if (!this.$store.getters.isProfileLoaded) {
      this.$store.dispatch(USER_REQUEST)
        .then(() => {
          this.fetchData()
        })
        .catch(() => {
          this.$router.push('/')
        })
    } else {
      this.fetchData()
    }
  }
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 20px;
}
.mt-3 {
  margin-top: 20px;
}
.park-card {
  width: 100%;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.park-card:hover {
  transform: translateY(-2px);
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.action-block {
  display: block;
}
.action-column {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.action-column .v-btn {
  width: 100%;
}
</style>
